import {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { SpotToast } from "../SpotToast";
import { StorageService } from "@/services/Storage/storage";
import { File as SpotFile } from "@/types";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { Paperclip, Pencil, Plus, X } from "lucide-react";
import { FileDropTarget } from "@/components/Form/FileDropTarget";
import { SelectedFileRow } from "@/components/Form/SelectedFileRow";
import { toSpotFiles } from "@/components/Form/spotFiles";
import Image from "next/image";
import { getSpotFileUrl } from "@/utils/generic";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { useSpotUser } from "@/utils/spotUser";
import { Button } from "../shadcn/button";
import { Avatar, AvatarFallback, AvatarImage } from "../shadcn/avatar";
import { Spinner } from "../UI/Spinner";
import { cn } from "@/lib/utils";
import {
  FileTypes,
  getAcceptAttributeValue,
  getFileTypeLabel,
  getMaxFileSize,
  areValidFileTypes,
} from "@/utils/mimetypes";
import { ImageCropDialog } from "./ImageCropDialog";
import { isCroppableImage } from "./imageCrop";

type FileUploadVariant = "image" | "logo" | "regular" | "inline";

const MAX_IMAGE_SIZE = 5242880;

// FileTypes.IMAGE is the umbrella type; spell out what it accepts.
const IMAGE_FORMAT_LABELS: Partial<Record<FileTypes, string>> = {
  [FileTypes.IMAGE]: "PNG, JPEG, GIF, TIFF, HEIC",
};

const imageFormatsLabel = (allowedFileTypes?: FileTypes[]): string =>
  Array.from(
    new Set(
      (allowedFileTypes ?? [FileTypes.IMAGE]).map(
        (type) => IMAGE_FORMAT_LABELS[type] ?? getFileTypeLabel(type)
      )
    )
  ).join(", ");

interface IFileUpload {
  setValues: (changes: SpotFile[]) => any;
  values?: SpotFile[];
  setValuesUploading?: (changes: SpotFile[]) => any;
  canSelectMultipleFiles?: boolean;
  allowed_file_types?: FileTypes[];
  isDisabled?: boolean;
  variant?: FileUploadVariant;
  imageProps?: Record<string, any>;
  showSelectedFiles?: boolean;
  isPublic?: boolean;
  // Upload into a specific group instead of the user's active group. Needed
  // when the file belongs to another scope (e.g. a workspace variable) so it
  // resolves under the group the flow actually runs in.
  uploadGroupId?: string;
}

export const FileUpload = ({
  values,
  setValues,
  setValuesUploading,
  canSelectMultipleFiles,
  allowed_file_types,
  isDisabled,
  variant = "regular",
  imageProps,
  showSelectedFiles = false,
  isPublic,
  uploadGroupId,
}: IFileUpload) => {
  const [loading, setLoading] = useState(false);
  const [loadingImage, setLoadingImage] = useState(true);
  const [isDragActive, setIsDragActive] = useState(false);
  const [pendingCrop, setPendingCrop] = useState<File | null>(null);
  const maxFileSize = variant === "image" ? MAX_IMAGE_SIZE : getMaxFileSize();
  const maxFileSizeLabel = `${maxFileSize / 1024 / 1024}MB`;

  // Scroll-edge fades for the capped inline file list: shown only when there
  // is more content in that direction.
  const fileListRef = useRef<HTMLDivElement>(null);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);

  const updateFileListFades = useCallback(() => {
    const el = fileListRef.current;
    if (!el) return;
    setShowTopFade(el.scrollTop > 0);
    setShowBottomFade(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const el = fileListRef.current;
    if (!el) return;
    updateFileListFades();
    el.addEventListener("scroll", updateFileListFades);
    return () => el.removeEventListener("scroll", updateFileListFades);
  }, [updateFileListFades, values?.length, showSelectedFiles]);

  const { spotGroup } = useSpotUser();

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = async (
    event
  ) => {
    processFiles(event.target.files);
  };

  const handleFileInputClick = () => {
    if (isDisabled) return;

    const tempInput = document.createElement("input");
    tempInput.type = "file";
    tempInput.multiple = canSelectMultipleFiles || false;
    tempInput.accept = getAcceptAttributeValue(allowed_file_types);
    tempInput.style.display = "none";

    tempInput.onchange = (event) => {
      handleOnChange(event as any);
      document.body.removeChild(tempInput);
    };

    document.body.appendChild(tempInput);
    tempInput.click();
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (isDisabled) return;
    setIsDragActive(true);
  };

  const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (isDisabled) return;
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (isDisabled) return;
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (isDisabled) return;
    setIsDragActive(false);
    const files = event.dataTransfer.files;
    processFiles(files);
  };

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (!areValidFileTypes(files, allowed_file_types)) {
      SpotToast.error("Invalid file type");
      return;
    }

    if (!canSelectMultipleFiles && files.length !== 1) {
      SpotToast.error("You can only upload one file.");
      return;
    }

    if (!checkFileSizes(Array.from(files))) return;

    if (
      variant === "image" &&
      files.length === 1 &&
      isCroppableImage(files[0])
    ) {
      setPendingCrop(files[0]);
      return;
    }

    await uploadFiles(Array.from(files));
  };

  const checkFileSizes = (files: File[]) => {
    if (files.every((file) => file.size <= maxFileSize)) return true;
    SpotToast.error(`File size exceeds the ${maxFileSizeLabel} limit.`);
    return false;
  };

  const uploadFiles = async (files: File[]) => {
    if (!checkFileSizes(files)) return;

    setLoading(true);
    try {
      const tmpFiles: SpotFile[] = [];
      const promisesArray = files.map((f) => {
        const data = new FormData();
        data.append("file", f);
        if (f.lastModified) {
          data.append(
            "client_last_modified",
            new Date(f.lastModified).toISOString()
          );
        }
        tmpFiles.push({
          uri: `spot://loading`,
          name: f.name,
          content_type: f.type,
          size: f.size,
        });
        return StorageService.uploadFile(
          uploadGroupId ?? spotGroup?.id,
          data,
          isPublic || false
        );
      });

      setValuesUploading?.(tmpFiles);

      const results = await Promise.all(promisesArray);

      setValues(
        results.map((sf) => ({
          uri: `spot://${sf.id}`,
          name: sf.filename,
          content_type: sf.content_type,
          size:
            sf && typeof sf === "object" && "size" in sf
              ? (sf.size as number)
              : undefined,
        }))
      );

      if (imageProps?.onChange) {
        imageProps.onChange(getSpotFileUrl(`spot://${results[0].id}`, true));
      }
      setValuesUploading?.([]);
    } catch (e) {
      if ((e as any)?.status === 413) {
        SpotToast.error(
          "The file you are trying to upload is too large. Please select a smaller file and upload again."
        );
      } else {
        SpotToast.error(
          "There was an error uploading the file. Please try again."
        );
      }
      console.error(e);
    }
    setLoading(false);
  };

  const hidePlusIcon =
    (values?.[0]?.uri || imageProps?.defaultImage) && variant === "image";

  const inlineTriggerLabel =
    showSelectedFiles || !values?.length
      ? canSelectMultipleFiles
        ? "Click to select files"
        : "Click to select file"
      : values.length === 1
        ? values[0].name || "1 file"
        : `${values.length} files`;

  return (
    <div className="w-full">
      {variant === "logo" ? (
        <FileDropTarget
          onClick={handleFileInputClick}
          onFiles={processFiles}
          loading={loading}
          isDisabled={isDisabled}
          allowedFileTypes={allowed_file_types}
          multiple={canSelectMultipleFiles}
        />
      ) : variant === "image" ? (
        <div className="flex gap-4 items-center justify-start">
          <div className="relative w-24 h-24 shrink-0 group">
            <button
              className={cn(
                "relative w-24 h-24 rounded-lg overflow-hidden bg-transparent hover:bg-transparent",
                isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              )}
              onClick={handleFileInputClick}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              type="button"
              disabled={isDisabled}
              data-testid="image-upload-trigger"
            >
              {imageProps?.profileName ? (
                <Avatar className="h-full w-full rounded-lg relative">
                  {loadingImage && imageProps?.defaultImage && (
                    <LoadingOverlay />
                  )}
                  <AvatarImage
                    src={
                      values?.[0]?.uri
                        ? getSpotFileUrl(values[0].uri, true)
                        : imageProps?.defaultImage
                    }
                    className="object-cover"
                    onLoad={() => setLoadingImage(false)}
                    onError={() => setLoadingImage(false)}
                  />
                  {!imageProps?.defaultImage && (
                    <>
                      <PencilOverlay />
                      <AvatarFallback className="bg-primary text-3xl text-primary-foreground rounded-lg">
                        {imageProps.profileName
                          ?.split(" ")
                          .map((n: string) => n[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)}
                      </AvatarFallback>
                    </>
                  )}
                </Avatar>
              ) : (
                <>
                  {values?.[0]?.uri || imageProps?.defaultImage ? (
                    <div className="relative border-border border rounded-lg w-full h-full flex items-center justify-center overflow-hidden">
                      {loadingImage && <LoadingOverlay />}
                      <div className="relative w-full h-full">
                        <Image
                          src={
                            values?.[0]?.uri
                              ? getSpotFileUrl(values[0].uri, true)
                              : imageProps?.defaultImage || ""
                          }
                          alt="Uploaded image"
                          fill
                          className="object-contain"
                          onLoad={() => setLoadingImage(false)}
                          onError={() => setLoadingImage(false)}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-background hover:bg-muted rounded-lg border border-dashed border-input">
                      <Plus size={24} strokeWidth="1.25px" />
                    </div>
                  )}
                </>
              )}
              {(loading || imageProps?.isLoading) && <LoadingOverlay />}
            </button>
            {hidePlusIcon && !(loading || imageProps?.isLoading) && (
              <>
                <PencilOverlay />
                {((values?.length ?? 0) > 0 || imageProps?.defaultImage) && (
                  <Button
                    aria-label="Remove image button"
                    className="bg-background border-none rounded-md shadow-md h-6 w-6 absolute right-0 top-0 p-0 hover:bg-background opacity-0 group-hover:opacity-90 hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      setValues([]);
                      if (imageProps?.onChange) {
                        imageProps.onChange([]);
                      }
                    }}
                    variant="secondary"
                    size="icon"
                    disabled={isDisabled}
                  >
                    <X size="1rem" strokeWidth="1.25px" />
                  </Button>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col justify-center items-start gap-0.5 text-xs text-muted-foreground">
            {imageProps?.description ? (
              <p>{imageProps.description}</p>
            ) : (
              <>
                <p>
                  Supported formats: {imageFormatsLabel(allowed_file_types)}
                </p>
                <p>Maximum size: {maxFileSizeLabel}</p>
              </>
            )}
          </div>
          {pendingCrop && (
            <ImageCropDialog
              file={pendingCrop}
              onConfirm={(file) => {
                setPendingCrop(null);
                uploadFiles([file]);
              }}
              onUseOriginal={() => {
                const file = pendingCrop;
                setPendingCrop(null);
                uploadFiles([file]);
              }}
              onCancel={() => setPendingCrop(null)}
            />
          )}
        </div>
      ) : variant === "inline" ? (
        <button
          type="button"
          disabled={isDisabled}
          onClick={handleFileInputClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            "flex h-8 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-transparent px-2.5 shadow-sm",
            "text-left text-sm transition-colors outline-none focus-visible:ring-ring focus-visible:ring-1",
            isDisabled
              ? "cursor-not-allowed opacity-50"
              : "cursor-pointer hover:bg-accent/40"
          )}
        >
          {loading ? (
            <Spinner size="xs" className="shrink-0 text-muted-foreground" />
          ) : (
            <>
              <Paperclip
                size={14}
                className="shrink-0 text-muted-foreground/70"
              />
              <span className="truncate text-muted-foreground">
                {inlineTriggerLabel}
              </span>
            </>
          )}
        </button>
      ) : (
        <div
          className={cn(
            "flex items-center justify-center text-center w-full h-[72px] rounded border border-dashed border-muted-foreground/50 hover:border-[#203D54]",
            isDisabled
              ? "cursor-not-allowed opacity-50 hover:border-muted-foreground/50"
              : "cursor-pointer"
          )}
          onClick={handleFileInputClick}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {loading ? (
            <Spinner />
          ) : (
            <p className="text-xs font-semibold">
              Drag and drop {canSelectMultipleFiles ? "files" : "a file"} or
              click to browse
            </p>
          )}
        </div>
      )}
      {showSelectedFiles && values && values.length > 0 && (
        <div className="relative mt-2">
          <div
            ref={fileListRef}
            className={cn(
              "flex w-full flex-col gap-1.5",
              // 4 rows (h-8) + gaps + half a 5th row, so overflow is visible
              // as a partially cut entry and the list scrolls.
              variant === "inline" && "max-h-[168px] overflow-y-auto"
            )}
          >
            {values.map((file, index) => (
              <SelectedFileRow
                key={file.uri}
                name={file.name}
                contentType={file.content_type}
                size={typeof file.size === "number" ? file.size : undefined}
                isDisabled={isDisabled}
                onRemove={() => setValues(values.filter((_, i) => i !== index))}
              />
            ))}
          </div>
          {variant === "inline" && showTopFade && (
            <div
              className="pointer-events-none absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-background to-transparent"
              aria-hidden
            />
          )}
          {variant === "inline" && showBottomFade && (
            <div
              className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background to-transparent"
              aria-hidden
            />
          )}
        </div>
      )}
    </div>
  );
};

interface IFileUploadField {
  name: string;
  canSelectMultipleFiles?: boolean;
  allowed_file_types?: FileTypes[];
  control: Control<any>;
  label?: string;
  isDisabled?: boolean;
  rules?: RegisterOptions;
  tooltip?: string;
  variant?: FileUploadVariant;
  imageProps?: Record<string, any>;
  defaultValue?: SpotFile[];
  onChange?: () => void;
  isPublic?: boolean;
}

export const FileUploadField = ({
  name,
  canSelectMultipleFiles,
  allowed_file_types,
  control,
  isDisabled,
  label,
  rules,
  tooltip,
  variant,
  imageProps,
  defaultValue,
  isPublic,
}: IFileUploadField) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field }) => {
        const values = toSpotFiles(field.value);

        return (
          <div className="w-full">
            {label != undefined && (
              <NoxusFormLabel label={label} tooltip={tooltip} name={name} />
            )}
            <FileUpload
              values={values}
              setValues={field.onChange}
              isDisabled={isDisabled}
              canSelectMultipleFiles={canSelectMultipleFiles}
              allowed_file_types={allowed_file_types}
              variant={variant}
              imageProps={imageProps}
              isPublic={isPublic}
              showSelectedFiles={variant !== "image"}
            />
          </div>
        );
      }}
    />
  );
};

const PencilOverlay = () => {
  return (
    <div className="absolute bottom-0 right-0 w-full h-full flex items-center justify-center bg-foreground opacity-0 group-hover:opacity-80 transition-opacity duration-200 cursor-pointer rounded-lg pointer-events-none active:opacity-100">
      <Pencil size="1.5rem" strokeWidth="1.5px" className="text-background" />
    </div>
  );
};

const LoadingOverlay = () => {
  return (
    <div className="absolute bg-primary opacity-80 inset-0 flex items-center justify-center z-10">
      <Spinner className="text-white" />
    </div>
  );
};
